import { type Scope, createContextScope } from '@radix-ui/react-context'
import { useState } from 'react'
import React from 'react'
import * as S from './accordion.styles'
import { Content } from './components/content'
import { Header } from './components/header'
import { Item } from './components/item'
import type { TAccordionProps } from './types'

const DISPLAY_NAME: string = 'Accordion'

type AccordionContextType = {
  expandedIndex: number | null
  setExpandedIndex: (index: number | null) => void
  disabled?: boolean
}

const Root = (props: TAccordionProps) => {
  const { children, testID, disabled, __scopeAccordion } =
    props as ScopedProps<TAccordionProps>
  const [expandedIndex, setExpandedIndex] = useState<number | null>(() => {
    const defaultIndex = React.Children.toArray(children).findIndex(
      child => React.isValidElement(child) && child.props.defaultExpanded
    )
    return defaultIndex !== -1 ? defaultIndex : null
  })

  return (
    <AccordionProvider
      scope={__scopeAccordion}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      disabled={disabled}
    >
      <S.Root testID={testID}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { index } as { index: number })
          }
          return child
        })}
      </S.Root>
    </AccordionProvider>
  )
}

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeAccordion?: Scope
}

const [createAccordionContext] = createContextScope(DISPLAY_NAME)

export const [AccordionProvider, useAccordionContext]: readonly [
  React.FC<
    AccordionContextType & {
      scope: Scope<AccordionContextType>
      children: React.ReactNode
    }
  >,
  (consumerName: string, scope: Scope) => AccordionContextType,
] = createAccordionContext<AccordionContextType>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const Accordion = {
  Root,
  Content,
  Header,
  Item,
}
